import React from 'react';
import type { Dayjs } from 'dayjs';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { CheckoutSummaryResult } from '../../generated/graphql';
import BookingDateTimeSelector from '../UI/BookingDateTimeSelector';
import DateSelectorWrapper from './DateSelectorWrapper';
import onSubmit from './helpers/onSubmit';
import { actions } from './helpers/datesReducer';
import convertToDayjs from '../../helpers/dates/convertToDayjs';

type PriceLookupResponse = {
  data: any,
  loading: boolean,
  error: any
};

export type Props = {
  listingId: number,
  onBeforeSubmit: () => void,
  dates: { startDateTime: ?Dayjs, endDateTime: ?Dayjs },
  datesDispatch: () => void,
  price: PriceLookupResponse
};

type UIProps = {
  isSubmitDisabled: boolean,
  onSubmitFixed: () => void,
  t: (string, string) => string,
  startTime: ?string,
  endTime: ?string,
  onStartTimeChange: () => void,
  onEndTimeChange: () => void,
  checkoutSummary: ?CheckoutSummaryResult,
  loading: boolean,
  error: ?string
};

const UI = ({
  t,
  startTime,
  endTime,
  onStartTimeChange,
  onEndTimeChange,
  checkoutSummary,
  loading,
  error,
  isSubmitDisabled,
  onSubmitFixed
}: UIProps) => (
  <DateSelectorWrapper
    checkoutSummary={checkoutSummary}
    loading={loading}
    error={error}
    t={t}
    isMonthly={false}
    isSubmitDisabled={isSubmitDisabled}
    onSubmit={onSubmitFixed}
  >
    <BookingDateTimeSelector
      startTimeLabel={t('dates:parkingFrom', 'Parking from')}
      endTimeLabel={t('dates:parkingUntil', 'Parking until')}
      startTime={startTime}
      endTime={endTime}
      onStartTimeChange={onStartTimeChange}
      onEndTimeChange={onEndTimeChange}
      datePlaceholder={t('dates:selectADate', 'Select a date')}
      timePlaceholder={t('dates:time', 'Time')}
    />
  </DateSelectorWrapper>
);

const FixedTermDateSelector = ({
  onBeforeSubmit,
  listingId,
  dates,
  datesDispatch,
  price
}: Props) => {
  const { t } = useTranslation();
  const { startDateTime, endDateTime } = dates;

  const setStartDateTime = (date) =>
    datesDispatch(actions.updateFixedStartDate(date));
  const setEndDateTime = (date) =>
    datesDispatch(actions.updateFixedEndDate(date));

  const navigate = useNavigate();

  const onSubmitFixed = () =>
    onSubmit({
      arriving: startDateTime,
      leaving: endDateTime,
      isMonthly: false,
      monthlyType: null,
      listingId,
      monthlyDuration: null,
      navigate,
      onBeforeSubmit
    });

  const { data, loading, error } = price;

  const checkoutSummary = data?.listing?.checkoutSummary;
  const isSubmitDisabled = checkoutSummary?.__typename !== 'CheckoutSummary';

  const onStartTimeChange = (value) => {
    if (endDateTime === null || endDateTime.isSameOrBefore(value)) {
      setEndDateTime(convertToDayjs(value).add(2, 'hours'));
    }

    return setStartDateTime(value);
  };

  const onEndTimeChange = (value) => {
    if (value.isSameOrBefore(startDateTime)) {
      return setEndDateTime(convertToDayjs(startDateTime).add(2, 'hours'));
    }
    return setEndDateTime(value);
  };

  return (
    <UI
      t={t}
      startTime={startDateTime}
      endTime={endDateTime}
      onStartTimeChange={onStartTimeChange}
      onEndTimeChange={onEndTimeChange}
      checkoutSummary={checkoutSummary}
      loading={loading}
      error={error}
      isSubmitDisabled={isSubmitDisabled}
      onSubmitFixed={onSubmitFixed}
    />
  );
};

export default FixedTermDateSelector;
